import {
  isNullOrUndefined
} from 'rewire-common';
import {
  arraySearch
} from 'rewire-ui';
import {
  BatchStatusType,
  BatchStatus,
  FieldFilterInput,
  EntityType,
  FieldFilter,
  IUdf,
} from '../DataTypes';
import DataQueries from '../graphql/Queries';
import { Deferred } from './Deferred';

export const boolSearcher = arraySearch([true, false], (item: any) => !isNullOrUndefined(item) ? (item ? 'Yes' : 'No') : '');

export function createUdfSearch(udf: IUdf, listMapper?: (v: any) => any) {
  const id = udf.id;
  return {
    async search(search?: string) {
      const r = await DataQueries.getUdfsValuesSearchQuery([id], search);
      if (r && listMapper) return r.map(listMapper);
      return r;
    },
    map(item: any) {
      if (Array.isArray(item)) return '';
      if (!item) return '';
      if (item.code && item.name) return `${item.code} - ${item.name}`;
      return item?.name || item || '';
    }
  };
}

export function getBatchSearchFilter(searchValues: any): FieldFilterInput {
  const filter: any = {};
  const status = parseStatus(searchValues);
  if (status) filter.batchStatus = status;
  if (searchValues.batches) filter.batchIds = [searchValues.batches.id];

  filter.tags = '';
  addValues('areas', 'areaIds', searchValues, filter);
  addValues('groups', 'groupIds', searchValues, filter);
  addValues('occupations', 'occupationIds', searchValues, filter);
  addValues('employees', 'employeeIds', searchValues, filter);
  return filter;
}

function addValues(valueKey: string, filterKey: string, values: any, filter: any) {
  if (values[valueKey]?.length) {
    filter[filterKey] = values[valueKey].map((key: any) => key.id);
  }
}

function parseStatus(values: any): BatchStatusType | undefined {
  if (!(values?.status)) return undefined;

  switch (values.status) {
    case BatchStatus.Closed:
      return 'closed';

    case BatchStatus.Exported:
      return 'exported';

    // case BatchStatus.NotInBatch:
    default:
      return 'not_in_batch';
  }
}

export type ISearchFn = (search?: string) => any;
export type IItemDisplayMapFn = (item?: any) => string;

function debounceAsync(fn: ISearchFn, ms: number = 250): ISearchFn {
  let _currentSearch: string | undefined;
  let _search: string | undefined;
  let _deferred: Deferred<any> | undefined;
  let _cache: { [item: string]: any } = {};
  let _focusEl: Element | null;
  let _handle: NodeJS.Timeout | undefined = undefined;

  // clear the cache on blur
  function clearCache() {
    _focusEl?.removeEventListener('blur', clearCache);
    _focusEl = null;
    _cache = {};
  }

  // actually perform the search saving the current search and storing the resolved promise in the cache.
  async function performSearch() {
    _currentSearch = _search;
    const p = fn(_search);
    const results = await p; // await for success before caching!
    if (_focusEl) _cache[_currentSearch!] = p;
    return results;
  }

  // start the timer. and perform the search if it expires
  function start() {
    if (_handle) return;
    _handle = setTimeout(async () => {
      const deferred = _deferred;
      _deferred = undefined;
      _handle = undefined;
      if (_currentSearch === _search) return;
      deferred?.resolve(await performSearch());
    }, ms);
  }

  // stop the time
  function stop() {
    if (_handle) clearTimeout(_handle);
    _handle = undefined;
  }

  async function s(search?: string) {
    // convert search to lower case or empty string
    _search = search?.toLowerCase() ?? '';

    // if has focus and is in cache return results!
    if (_focusEl) {
      const _cached = _cache[_search];
      if (_cached) return _cached;
    } else { // get the focus element for this search and clear the cache on blur.
      _focusEl = document.activeElement;
      _focusEl?.addEventListener('blur', clearCache);
    }

    // if we have started the clock and are currently executing the same search item don't abort the search.
    if (_deferred && (_currentSearch === _search)) return _deferred.promise;
    // first time after or first time after a  time out!
    if (!_deferred) {
      _deferred = new Deferred();
      start();
      return performSearch();
    }

    // restart timeout if typing during the deferred period.
    stop();
    start();

    // return the future promise
    return _deferred?.promise;
  }
  return s;
}

export function nameSearcher(searchFn: ISearchFn, itemDisplayMapper?: IItemDisplayMapFn) {
  const fn = debounceAsync(searchFn);
  return {
    search(search?: string) {
      return fn(search);
    },
    map(item: any) {
      if (itemDisplayMapper) {
        return itemDisplayMapper(item);
      }
      return item?.display ?? item?.name ?? '';
    }
  };
}

const defaultNameSearchReturnSchema = `
  id
  name
  code
  display
`;

export const nameSearch = (type: EntityType, filter?: () => FieldFilter | undefined, schema?: string, itemDataMapper?: (item: any) => any, count?: number) => async (search?: string) => {
  const results = await DataQueries.getSearch(schema || defaultNameSearchReturnSchema, type, search, filter?.(), count);
  return itemDataMapper ? results.map(itemDataMapper) : results;
};

export const getNameSearcher = (type: EntityType, filter?: () => FieldFilter | undefined, schema?: string, itemDataMapper?: (item: any) => any, itemDisplayMapper?: (item: any) => string, count?: number) => nameSearcher(nameSearch(type, filter, schema, itemDataMapper, count), itemDisplayMapper);
export const roleSearcher = getNameSearcher('roles');

const defaultMap = (value: any) => (value?.display) || (value?.name) || value || '';
const codeAndNameMap = (value: any) => {
  if (!value) return '';
  if (value?.code) {
    if (value?.name) return `${value.code} - ${value.name}`;
    return value.code;
  }
  if (value?.name) return value.name;
  return value ?? '';
};
export const getArrayStringMap = (map?: (value: any) => any) => (value: any) => {
  if (!value) return '';
  if (!Array.isArray(value)) value = [value];

  const values = (map) ? value.map((v: any) => map(v)) : value;
  return values.join(', ');
};

export const getArrayString = getArrayStringMap(defaultMap);
export const getTemplateArrayString = getArrayStringMap(codeAndNameMap);
